import clsx from 'clsx';
import { CSSProperties, FC, Ref } from 'react';
import { IconSVG } from 'atoms/IconSVG';
import { Image } from 'atoms/Image/Image';
import { Link } from 'atoms/Link/Link';
import { Logo } from 'molecules/Logo/Logo';
import { HeaderState } from 'organisms/Header/Header';
import { SearchIcon } from 'organisms/Header/Icon/SearchIcon';
import { WishlistIcon } from 'organisms/Header/Icon/WishlistIcon';
import { LoginIcon } from 'organisms/Header/Navigation/LoginIcon';
import { useGTMCurrentBasket } from 'modules/gtm/modules/sections/cart/hooks';
import {
  gtmPushBurgerMenuMobile,
  gtmPushShopNavigation,
} from 'modules/gtm/modules/sections/navigation/navigation';
import { useGTMCurrentWishlist } from 'modules/gtm/modules/sections/wishlist/hooks';
import { useTranslations } from 'modules/i18n/useTranslations';
import { useMediaQuery } from 'modules/mui/hooks';
import { usePopoverManager } from 'modules/shop/popoverManager/hooks';
import { PopoverType } from 'modules/shop/popoverManager/types';
import { HEADER_LINE_ID } from 'src/client/components/constants';
import { HeaderSearch } from '../../HeaderSearch';
import { CartIcon } from '../Icon/CartIcon';
import styles from './HeaderLine.module.css';

const LogoLink: FC<{ promoLogo?: { src?: string; alt?: string } }> = ({
  promoLogo,
}) => {
  const t = useTranslations();
  const handleLogoClick = () =>
    gtmPushShopNavigation(t('header.gtmNavigationLabels.HomeLogo'));

  return (
    <Link href="/" className={styles.logo} onClick={handleLogoClick}>
      {promoLogo?.src ? (
        <Image
          className={styles.logoImg}
          src={promoLogo?.src}
          alt={promoLogo?.alt}
        />
      ) : (
        <Logo className={styles.logoImg} />
      )}
    </Link>
  );
};

export const HeaderLineSearchField: FC<{
  ref?: Ref<HTMLDivElement> | null;
  isFixed?: boolean;
  style?: CSSProperties;
}> = ({ ref, isFixed, style }) => {
  return (
    <div
      ref={ref}
      id={HEADER_LINE_ID}
      className={clsx(styles.menu, isFixed && styles.menuFixed)}
      style={style}
    >
      <HeaderSearch className={styles.search} />
      <div className={styles.menuIcons}>
        <WishlistIcon className={styles.icon} />
        <LoginIcon />
        <CartIcon className={styles.icon} />
      </div>
    </div>
  );
};

export const HeaderLine: FC<{
  state?: HeaderState;
  isSimple?: boolean;
  promoLogo?: { src?: string; alt?: string };
  hideSearchIcon?: boolean;
}> = ({ state, isSimple, promoLogo, hideSearchIcon }) => {
  const { isMobile } = useMediaQuery();

  const { openSidebarPopover, activePopover } = usePopoverManager();

  useGTMCurrentWishlist();
  useGTMCurrentBasket();

  const handleBurgerClick = () => {
    gtmPushBurgerMenuMobile();
    openSidebarPopover();
  };

  if (isSimple) {
    return (
      <div className={clsx(styles.root, styles.simple)}>
        <LogoLink promoLogo={promoLogo} />
      </div>
    );
  }

  return (
    <div className={clsx(styles.root, state?.isSticky && styles.sticky)}>
      <LogoLink promoLogo={promoLogo} />

      {!isMobile && <HeaderLineSearchField />}

      <div className={styles.icons}>
        <SearchIcon className={clsx(hideSearchIcon && styles.hideSearchIcon)} />
        <WishlistIcon className={styles.icon} />
        <CartIcon className={styles.icon} />
        {activePopover === PopoverType.SIDEBAR ? (
          <IconSVG
            type="close"
            className={styles.burger}
            onClick={handleBurgerClick}
          />
        ) : (
          <IconSVG
            type="burger"
            className={styles.burger}
            onClick={handleBurgerClick}
          />
        )}
      </div>
    </div>
  );
};
